/* eslint-disable @typescript-eslint/naming-convention */
import { FormGroup } from '@angular/forms';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { ConfigurationAlert, JsonReply, NextLocator, Partaker, SelectOption } from '@grupo-constant/next-dtype/types';
import { NextBrand, NextCustomer, NextProject, UserNodePrivileges, UserProfile } from '../projects/projects.types';
import { TaskStatusEnum } from '../tasks/tasks.types';
import { CompanyUser } from '../../../core/user/companyUser.model';
import { LocaleString } from '../../../core/translate/localeString.model';
import { NextTaskType, TaskTypeTypology } from '../../../core/task-type/task-type.types';

export const backofficeProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileBackoffice';
export const viewerProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileViewer';

export enum ServiceStatusEnum {
    design = 'DESIGN',
    planning = 'PLANNING',
    inProgress = 'IN_PROGRESS',
    finished = 'FINISHED',
    abandoned = 'ABANDONED',
    historical = 'HISTORICAL'
}

export interface ServiceState {
    status: ServiceStatusEnum;
    label: string;
}

export const ServiceStatus: Record<ServiceStatusEnum, ServiceState> = {
    'DESIGN': { status: ServiceStatusEnum.design, label: 'design' },
    'PLANNING': { status: ServiceStatusEnum.planning, label: 'planning' },
    'IN_PROGRESS': { status: ServiceStatusEnum.inProgress, label: 'inProgress' },
    'FINISHED': { status: ServiceStatusEnum.finished, label: 'finished' },
    'ABANDONED': { status: ServiceStatusEnum.abandoned, label: 'abandoned' },
    'HISTORICAL': { status: ServiceStatusEnum.historical, label: 'historical' }
};

export enum ServiceTaskView {
    TASKLIST = 'TASKLIST',
    USERLIST = 'USERLIST'
}

export enum ServiceTypology {
    PLANNINGAREA = 'PLANNINGAREA',
    CENTER = 'CENTER',
    WORKPLACES = 'WORKPLACES',
    HOUSEKEEPING = 'HOUSEKEEPING'
}

export interface NextServicio {
    serviceId: string;
    marca_code?: string;
    cliente_code?: number;
    projectId?: string;
    name?: string;
    from?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: ServiceStatusEnum;
    statusDate?: string;
    statusUserId?: string;
    weekDays?: string[];
    useZoneHolidayCalendar?: boolean;
    useLocationCalendar?: boolean;
    allowSetHolidayInPlanning?: boolean;
    generateUsernamesManually?: boolean;
    userId?: string;
    datatypeUri?: string;
    uriPlanningArea?: string;
    uriWorkplaces?: string;
    absencesUri?: string;
    payload?: string;
    prefix?: string;
    serviceTaskView?: ServiceTaskView;
    role?: UserNodePrivileges;
    backoffice?: UserProfile[];
    viewer?: UserProfile[];
    [backofficeProfileUri]?: CompanyUser[] | string[] | UserProfile[];
    [viewerProfileUri]?: CompanyUser[] | string[] | UserProfile[];
    transitions?: string[];
    active?: boolean;
    typology?: ServiceTypology;
    genericApplicationName?: GenericApplicationName;
    mailDebugModeUsers?: CompanyUser[] | string[];
    mailBCCUsers?: CompanyUser[] | string[];
    sendQRMail?: boolean;
    sendJoinThePartyMail?: boolean;
    canCreateTask: boolean;
    showGoalsUpload?: boolean;
    breakfast: boolean;
    breakfastDuration: number;
    breakfastName: string;
    lunch: boolean;
    lunchDuration: number;
    lunchName: string;
    lunchWorkDaySplit: boolean;

    // Partaker info.
    brand?: Partial<NextBrand>;
    customer?: Partial<NextCustomer>;
    project?: NextProject;

    statusLabel?: string;
}

export enum GenericApplicationName {
    next = "com.grupoconstant.next",
    staffHotel = "com.grupoconstant.staffhotel",
    weldon = "com.grupoconstant.nextweldon",
    gesgrup = "com.grupoconstant.nextgesgrup"
}

export interface AvailabilityControl {
    formOid: string;
    formUri: string;
    userId: string;
    userName: string;
    absenceType: string;
    from: string;
    to: string;
    fromTime: string;
    toTime: string;
    weekDays?: string[];
    canceledDates?: string[];
    documents: string[];
    senderComment: string;
    reviewComment: string;
    status?: AvailabilityStatusEnum;
    receivedTimestamp?: string;
    senderUserId?: string;
    reviewTimestamp?: string;
    reviewUserId?: string;
    dtypeForm?: DTypeForm;
    lastRequestTimestamp: string;
    lastReviewTimestamp: string;
    serviceId?: string[];
    services?: NextServicio[];
}

export enum AvailabilityStatusEnum {
    requested = 'REQUESTED',
    pending = 'PENDING',
    approved = 'APPROVED',
    rejected = 'REJECTED'
}

export interface RetrieveNextServicioReply extends JsonReply {
    nextServicio: NextServicio;
    partaker: Partaker;
}

export interface RetrieveNextServicioListReply extends JsonReply {
    nextServicio: NextServicio[];
    partaker: Partaker;
    size: number;
}

export interface FlushNextServicioReply extends JsonReply {
    nextServicio: NextServicio;
    partaker: Partaker;
}

export interface FlushUser2NextServicioReply extends JsonReply {
    user2NextServicio: UserProfile[];
    size: number;
    partaker: Partaker;
}

export interface ChangeServiceStatusReply extends JsonReply {
    nextServicio: NextServicio;
    partaker: Partaker;
}

export interface RetrieveAbsenceFormsRequest {
    locator: NextLocator;
    from: string;
    to: string;
    status: AvailabilityStatusEnum[];
    absenceType: string[];
    userId: string[];
    supervisorId: string[];
}

export interface RetrieveAbsenceFormReply extends JsonReply {
    availabilityControl: AvailabilityControl[];
    partaker: Partaker;
}

export interface FlushAbsenceReviewRequest {
    formOid: string;
    status: AvailabilityStatusEnum;
    comments: string;
    deleteTasks?: boolean;
}

export interface FlushAbsenceReviewReply extends JsonReply {
    availabilityControl: AvailabilityControl;
}

export interface CancelAbsenceDayRequest {
    formOid?: string;
    userId: string;
    date: string;
    absenceType: string;
    serviceId: string;
    dailyTaskId: string;
}

export interface CancelAbsenceDayReply extends JsonReply {
    absencePayload: {
        type: string;
        date: string;
        fromTime?: string;
        toTime?: string;
        wholeDay: boolean;
        confirmed: boolean;
        locked: boolean;
        formOid?: string;
    }[];
    dayOff: boolean;
    absentAllDay: boolean;
}

export class ServicesBoard {
    brand: NextBrand;
    client: NextCustomer;
    project: NextProject;
    lists: List[];
}

export class List {
    position: number;
    title: string;
    status: ServiceStatusEnum;
    dragHighlight: boolean;
    dropHighlight: boolean;
    cards: ServiceCard[];

    constructor(title: string, status: ServiceStatusEnum, services: NextServicio[]) {
        this.title = title;
        this.status = status;
        this.cards = [];

        if (services) {
            this.cards = services.map((service) => {
                if (!(service instanceof ServiceCard)) {
                    return new ServiceCard(service);
                }

                return service;
            });
        }
    }
}

export class ServiceCard implements NextServicio {
    serviceId: string;
    marca_code: string;
    cliente_code: number;
    projectId: string;
    name: string;
    from: string;
    dateFrom: string;
    dateTo: string;
    status: ServiceStatusEnum;
    statusDate: string;
    statusUserId: string;
    weekDays: string[];
    useZoneHolidayCalendar: boolean;
    useLocationCalendar: boolean;
    allowSetHolidayInPlanning: boolean;
    userId: string;
    datatypeUri: string;
    uriPlanningArea: string;
    payload: string;
    role: UserNodePrivileges;
    backoffice: UserProfile[];
    viewer: UserProfile[];
    [backofficeProfileUri]: string[] | CompanyUser[] | UserProfile[];
    [viewerProfileUri]: string[] | CompanyUser[] | UserProfile[];
    transitions: string[];
    active: boolean;
    statusLabel: string;
    typology: ServiceTypology;
    canCreateTask: boolean;
    breakfast: boolean;
    breakfastDuration: number;
    breakfastName: string;
    lunch: boolean;
    lunchDuration: number;
    lunchName: string;
    lunchWorkDaySplit: boolean;

    constructor(service: NextServicio) {
        this.serviceId = service.serviceId;
        this.marca_code = service.marca_code;
        this.cliente_code = service.cliente_code;
        this.projectId = service.projectId;
        this.name = service.name;
        this.from = service.from;
        this.dateFrom = service.dateFrom;
        this.dateTo = service.dateTo;
        this.status = service.status;
        this.statusDate = service.statusDate;
        this.statusUserId = service.statusUserId;
        this.weekDays = service.weekDays;
        this.useZoneHolidayCalendar = service.useZoneHolidayCalendar;
        this.useLocationCalendar = service.useLocationCalendar;
        this.allowSetHolidayInPlanning = service.allowSetHolidayInPlanning;
        this.userId = service.userId;
        this.datatypeUri = service.datatypeUri;
        this.payload = service.payload;
        this.role = service.role;
        this.backoffice = service.backoffice;
        this.viewer = service.viewer;
        this.transitions = service.transitions;
        this.active = service.active;
        this.statusLabel = service.statusLabel;
        this.typology = service.typology;
        this.canCreateTask = service.canCreateTask;
    }
}

export interface ServiceCardList {
    serviceId: string;
    name: string;
    status: string;
    dateFrom: string;
    dateTo: string;
    margin: string;
    typology: ServiceTypology;
    role: UserNodePrivileges;
}

export interface PlanningArea {
    marca_code?: string;
    cliente_code?: number;
    projectId?: string;
    serviceId?: string;
    planningArea?: string;
    prefix?: string;
    numberOfZombies?: number;
    name?: LocaleString;
    resources?: { zombieNumber: number; label: string; selected: string }[];
    formGroup?: FormGroup;
    uri?: string;
    prefixRep?: boolean;
    calendarOid?: string;
}

export interface RetrievePlanningAreaListReply extends JsonReply {
    uriPlanningArea?: string;
    planningArea?: PlanningArea[];
    partaker?: Partaker;
}

export interface FlushPlanningAreaRequest {
    serviceId: string;
    planningArea: string;
    prefix: string;
    numberOfZombies?: number;
    calendarOid?: string;
}

export interface FlushPlanningAreaReply extends JsonReply {
    uriPlanningArea?: string;
    planningArea?: PlanningArea;
    partaker?: Partaker;
}

export interface NextServicioUserAreaFront {
    marca_code?: string;
    cliente_code?: number;
    projectId?: string;
    serviceId: string;
    odc_code: string;
    cepoblacion: string;
    numContratos?: number;
    planningArea: string[];
    userId?: string;

    // Info: only for app. Not present on the NextServicioUserAreaFront specification
    odc_name?: string;
    planningAreaForDisplay?: SelectOption[];
}

export interface RetrieveUserAreaListReply extends JsonReply {
    uriPlanningArea?: string;
    userArea?: NextServicioUserAreaFront[];
    partaker?: Partaker;
}

export interface FlushUserAreaBody {
    serviceId?: string;
    odc_code?: string;
    cepoblacion?: string;
    planningArea?: string[];
}

export interface BusOdc {
    odc_code?: string;
    odc_name?: string;
    odc_delegacion_code?: string;
    odc_marca_code?: string;
    odc_ceco_code?: string;
    displayName?: string;
    sucursal?: { [key: string]: number }[];
}

export interface SearchBusOdcReply extends JsonReply {
    busOdc?: BusOdc[];
}

export interface Worker {
    hrid: string;
    trcodigo: number;
    nif: string;
    userId: string;
    name: string;
    from: string;
    to: string;
    available: SearchWorkerResultAvailable;
    assignedToThisService: boolean;
    assignedToOtherService: boolean;
    alert: ConfigurationAlert[];

    // Info: only for app. Not present on the SearchWorkerResult specification
    showInfo?: boolean;
}

export interface SearchWorkers {
    expr: string;
    onlyNotAssociated: boolean;
    onlyAvailable: boolean;
    searchMyOdcOnly: boolean;
    searchWithoutHrid: boolean;
    serviceId: string;
    planningArea: string;
}

export interface SearchWorkersReply extends JsonReply {
    size: number;
    workers: Worker[];
    partaker: Partaker;
}

export enum SearchWorkerResultAvailable {
    yes = 'YES',
    partial = 'PARTIAL'
}

export interface ServiceUserAssignment {
    belongsTo?: string;
    serviceId?: string;
    serviceName?: string;
    planningArea?: string;
    planningAreaName?: string;
    userId: string;
    sAMAccountName: string;
    companyId?: string;
    from: string;
    to: string;
    jobName?: string;
    userAlias?: ServiceUserAssignmentAlias[];
    zombieNumber?: number;
    deprecated: boolean;
    userName?: string;
    hrid?: string;
    trained?: boolean;
    alert: ConfigurationAlert[];
    supervised: string[];
    supervisedView: string[];
    supervisor: string[];
    supervisorView: string[];
    //this fields will be added from partaker
    telephoneNumber?: string;
    userMail?: string;
    // Show alert tooltip: only for app. Not present on the ServiceUserAssignment specification
    showAlert?: boolean;
}

export interface ServiceUserAssignmentAlias {
    serviceId: string;
	planningAreaId: string;
    userId: string;
    userAlias: string;
	from: string;
	to?: string;
}

export interface ServiceUserAssignmentView extends ServiceUserAssignment {
    hide: boolean;
}

export interface ServiceUserAssignmentReply {
    belongsTo?: string;
    serviceId?: string;
    serviceName?: string;
    planningArea?: string;
    planningAreaName?: string;
    userId: string;
    sAMAccountName: string;
    companyId?: string;
    from: string;
    to: string;
    jobName?: string;
    userAlias?: ServiceUserAssignmentAlias[];
    zombieNumber?: number;
    deprecated: boolean;
    trained?: boolean;
    alert: ConfigurationAlert[];
    supervised: string[];
    supervisor: string[];
}

export interface ServiceTaskTypeAssignment {
    serviceId: string;
    taskTypeUri: string;
    payload: NextTaskType;
    name?: string;
    id?: string;
}
export interface MissionInfo {
    projectId: string;
    serviceId: string;
    title: string;
    summary: LocaleString;
    body: LocaleString;
    image: string[];
    file: string[];
    showPlannerInfo: boolean;
    showServiceDates: boolean;
    userId: string;
    lastTimestamp: string;
}

export type ServiceUserAssignmentSort = 'NAME_THEN_ZONE' | 'ZONE_THEN_NAME' | 'ZOMBIE';

export interface RetrieveServiceUserAssignmentBody {
    serviceId: string;
    sort?: ServiceUserAssignmentSort;
}

export interface RetrieveServiceUserAssignmentReply extends JsonReply {
    assignment: {
        assignment: {
            [key: string]: ServiceUserAssignmentReply[];
        };
    };
    size: number;
    partaker: Partaker;
}

export interface ServiceUserAssignmentWorkerByPlanningArea {
    planningArea: string;
    prefixPlanningArea: string;
    hrid: string;
    zombieNumber?: number;
    trained: boolean;
    jobName?: string;
    userAlias?: ServiceUserAssignmentAlias[];
    supervised: string[];
}
export interface ServiceUserAssignmentZombiesByPlanningArea {
    planningArea: string;
    prefixPlanningArea: string;
    numberOfZombies: number;
}
export interface FlushServiceUserAssignmentListBody {
    serviceId: string;
    prefixBrandOrProject?: string;
    zombies?: ServiceUserAssignmentZombiesByPlanningArea[];
    workers?: ServiceUserAssignmentWorkerByPlanningArea[];
}

export interface FlushServiceUserAssignmentListReply extends JsonReply {
    retrieveServiceUserAssignmentListReply: RetrieveServiceUserAssignmentReply;
}

export interface DismissServiceUserAssignmentRequest {
    serviceId: string;
	planningArea: string;
	userId: string;
}

export interface RetrieveNextServicioMissionInfoBody {
    serviceId: string;
}
export interface RetrieveNextServicioMissionInfoReply extends JsonReply {
    missionInfo: MissionInfo;
}

export interface FlushNextServicioMissionInfoBody {
    projectId: string;
    serviceId: string;
    title: string;
    summary: LocaleString;
    body: LocaleString;
    image: string[];
    file: string[];
    showPlannerInfo: boolean;
    showServiceDates: boolean;
    userId: string;
    lastTimestamp: string;
}
export interface FlushNextServicioMissionInfoReply extends JsonReply {
    missionInfo: MissionInfo;
}

export interface DeleteNextServicioMissionInfoReply {
    status: string;
}

export interface TableInfo {
    OU: string;
    marca_code?: string;
    cliente_code?: number;
    projectId?: string;
    serviceId?: string;
    name: LocaleString;
    schema?: string;
    tableName?: string;
    type: TableInfoTypeEnum;
    typeCaption?: LocaleString | string;
    uri: string;
    uriComplex?: string;
    derivedFields?: string[];
    fields?: {
        fieldName: string;
        uri: string;
    }[];
}

export enum TableInfoTypeEnum {
    PLANNED_TASK = 'PLANNED_TASK',
    COMPLETED_TASK = 'COMPLETED_TASK',
    FORM = 'FORM',
    FORM_DERIVED = 'FORM_DERIVED',
    COMPLEX = 'COMPLEX',
    MASTER_GENERAL = 'MASTER_GENERAL',
    MASTER_PLANNINGZONES = 'MASTER_PLANNINGZONES',
    MASTER_LOCATION = 'MASTER_LOCATION',
    MASTER_WORKDAYTYPE = 'MASTER_WORKDAYTYPE',
    MASTER_CATALOG = 'MASTER_CATALOG',
    MASTER_PROFESSIONAL_CATEGORY = 'MASTER_PROFESSIONAL_CATEGORY'
}

export interface RetrieveNextServicioTableInfoBody {
    serviceId: string;
}
export interface RetrieveNextServicioTableInfoReply extends JsonReply {
    serviceTableInfo: TableInfo[];
}

export interface Pagination {
    length: number;
    size: number;
    page: number;
    lastPage: number;
    startIndex: number;
    endIndex: number;
}

export interface ServiceListFilters {
    status?: string[];
    dateFrom?: string;
    dateTo?: string;
}

export interface SearchAreaReply {
    _oid: string;
    prefix?: string;
    description: LocaleString;
    _deprecated: boolean;
}

export interface CloneServiceBody {
    originalServiceId: string;
    newServiceName: string;
}

export interface CloneServiceReply  {
    nextServicio: NextServicio;
    permissionsError: boolean;
    missionInfoError: boolean;
    masterError: boolean;
    taskTypesError: boolean;
    planningAreasError: boolean;
    userAreasError: boolean;
    missionUserAssignmentError: boolean;
}

export interface ImportNextServicioBody {
    projectId?: string;
    base64?: string;
}

export interface ImportNextServicioResponse extends JsonReply {
        nextServicio: NextServicio;
}

export interface RetrieveWorkDayDateCalendar {
    serviceId: string;
    date: string;
    planningAreaId?: string[];
    userId?: string[];
}
export interface RetrieveWorkDayDateCalendarReply {
    partaker: Partaker;
    calendar: Calendar;
}

export interface RetrieveWorkDayUserCalendar {
    serviceId: string;
    userId: string;
    from: string;
    to: string;
}
export interface RetrieveWorkDayUserCalendarReply {
    partaker: Partaker;
    calendar: Calendar;
}

export interface Calendar {
    workDayEvent: WorkDayEvent[];
    workDayEventTask: WorkDayEventTask[];
}

export interface WorkDayEvent {
    userId: string;
    sAMAccountName: string;
    serviceId?: string;
    workplaceCode?: string;
    type: WorkDayEventType;
    pauseReason?: PauseReason;
    queueTimestamp: string;
    queueDate: string;
    creationDate: string;
    filePath: string;
    kilometers: string;
    addressCiCoUri?: string;
    addressCiCoOid?: string;
    geopositionTimestamp?: string;
	latitude?: number;
	longitude?: number;
}

export interface WorkDayEventTask {
    marca_code: string;
    cliente_code: number;
    projectId: string;
    serviceId: string;
    taskId: string;
    order: number;
    startTaskXmlDate: string;
    startTaskDate: string;
    startTaskDate2?: string;
    endTaskDate: string;
    endTaskDate2?: string;
    status: TaskStatusEnum;
    userId: string;
    planningAreaId: string;
    taskTypeName: string;
    taskTypeTypology: TaskTypeTypology;
    scheduled_from_date: string;
    scheduled_to_date: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    scheduled_from_time2: string;
    scheduled_to_time2: string;
    addressName: string;
    addressVicinity: string;
}

export enum WorkDayEventType {
    START_WORKDAY = 'START_WORKDAY',
    PAUSE_WORKDAY = 'PAUSE_WORKDAY',
    RESTART_WORKDAY = 'RESTART_WORKDAY',
    END_WORKDAY = 'END_WORKDAY',
    RESET_END_WORKDAY = 'RESET_END_WORKDAY'
}

export enum DisplayWorkDayEventType {
    START_WORKDAY = 'START_WORKDAY',
    PAUSE_WORKDAY = 'PAUSE_WORKDAY',
    RESTART_WORKDAY = 'RESTART_WORKDAY',
    END_WORKDAY = 'END_WORKDAY',
    RESET_END_WORKDAY = 'RESET_END_WORKDAY',
    PAUSE_RESTART = 'PAUSE_RESTART'
}

export enum PauseReason {
    BREAKFAST = 'BREAKFAST',
    LUNCH = 'LUNCH',
    ANY = 'ANY'
}

export interface RetrieveServiceJobListReply extends JsonReply {
    job: ServiceJob[];
    size: number;
}

export interface FlushServiceJobReply extends JsonReply {
    job: ServiceJob[];
    size: number;
}

export interface DeleteServiceJobReply extends JsonReply {
    deletedJobKey: string[];
    size: number;
}

export interface ServiceJob {
    key?: string;
    marca_code: string;
    cliente_code: number;
    projectId: string;
    serviceId: string;
    name: string;
    isDefault: boolean;
    supervisor: boolean;
    nonProductive: boolean;
    description: LocaleString;

    //* Only for painting the job table
    dtypeForm?: DTypeForm;
}
export interface DownloadResourcesModelBody {
    serviceId: string;
    language?: string;
}

export interface UploadResourcesExcelBody {
    locator: NextLocator;
    excelType: string;
    async?: boolean;
    file?: string;
}

export interface UploadResourcesModelResponse extends JsonReply {
    errors?: boolean;
    async?: boolean;
    forcedAsync?: boolean;
    file?: string;
}

export interface RequestServiceSynchronizationBody {
    serviceId: string;
}

export interface RequestServiceSynchronizationReply extends JsonReply {
    usersWithoutFCMToken?: CompanyUser[];
}

export interface RetrieveAvailabilityControlSearchOptionsReply extends JsonReply {
    userIds: SelectOption[];
    supervisorIds: SelectOption[];
    absenceTypes: SelectOption[];
    statuses: SelectOption[];
}

export interface PresenceConfiguration {
    serviceId: string;
    sendToNet4: boolean;
    nocturnalFromTime: string;
    nocturnalToTime: string;
    validators: CompanyUser[] | string[];
}

export interface PresenceConcept {
    oid?: string;
    serviceId: string;
    name: string;
    request: PresenceConceptsIntervalsEnum;
    type: PresenceConceptTypeEnum;
    dispatch: PresenceConceptsIntervalsEnum;
    concept: PresenceConceptsEnum;
    readOnly: boolean;
    isPreviewHidden: boolean;
    userJob: string[];
    clienteTipoAsignacion: ClienteTipoAsignacionEnum[];
}

export enum PresenceConceptTypeEnum {
    BOOLEAN = 'BOOLEAN',
    INTEGER = 'INTEGER',
    HOUR = 'HOUR',
    WORKDAY = 'WORKDAY',
    MONTH = 'MONTH',
    DOUBLE = 'DOUBLE',
    PERCENT = 'PERCENT'
}

export enum ClienteTipoAsignacionEnum {
    CPD = 'CPD',
    CSE = 'CSE'
}

export enum PresenceConceptsEnum {
    CALCULATED_GOAL = 'CALCULATED_GOAL',
    KM = 'KM',
    OTHER_USER_CONCEPT = 'OTHER_USER_CONCEPT',
    HOLIDAY = 'HOLIDAY',
}

export enum PresenceConceptsIntervalsEnum {
    DAILY = 'DAILY',
    MONTHLY = 'MONTHLY'
}

export interface RetrievePresenceConfigurationReply extends JsonReply {
    configuration: PresenceConfiguration;
    concepts: PresenceConcept[];
    partaker: Partaker;
}

export interface FlushPresenceConfiguration {
    configuration: PresenceConfiguration;
    concepts: PresenceConcept[];
}

export interface DailyPayroll {
    cliente_code: number;
	projectId: string;
	serviceId: string;
    empresaId: number;
    assignmentOid: string;
    clienteTipoAsignacion: ClienteTipoAsignacionEnum;
	userId: string;
    userName: string;
    userSAMAccountName: string;
	externalCode: string;
	date: string;
    zombieName: string;
    userJob: string;
    numberOfTasks: number;
    validatorPayload: {
        absencePayload: DailyPayrollAbsencePayload[];
        payrollPayload: PayrollPayload[];
    };
	plannedMinutes: number;
    plannedBreakMinutes: number;
    plannedFromTime: string;
    plannedToTime: string;
	workedMinutes: number;
    workedBreakMinutes: number;
    workedFromTime: string;
    workedToTime: string;
	customMinutes: number;
    customBreakMinutes: number;
    customFromTime: string;
    customToTime: string;
    validatorSelection: 'PLANNED' | 'WORKED' | 'CUSTOM';
    validatorMinutes: number;
    validatorBreakMinutes: number;
    validatorFromTime: string;
    validatorToTime: string;
    sentToDeviceTimestamp: string;
	validationTimestamp: string;
    validatorUserId: string;
    validatorUserName: string;
    validatorOrigin: string;
	sentTimestamp: string;
	closed: boolean;
    readOnly: boolean;
    supervisor: string;
    supervisorName: string;
    comments: string;
}

export interface MonthlyPayroll {
    cliente_code: number;
	projectId: string;
	serviceId: string;
    empresaId: number;
    assignmentOid: string;
    clienteTipoAsignacion: ClienteTipoAsignacionEnum;
	userId: string;
    userName: string;
    userSAMAccountName: string;
	externalCode: string;
	year: string;
    period: string;
    zombieName: string;
    userJob: string;
    numberOfTasks: number;
    validatorPayload: {
        payrollPayload: PayrollPayload[];
    };
    sentToDeviceTimestamp: string;
	validationTimestamp: string;
    validatorUserId: string;
    validatorUserName: string;
    validatorOrigin: string;
	sentTimestamp: string;
	closed: boolean;
    readOnly: boolean;
    supervisor: string;
    supervisorName: string;
    comments: string;
}

export interface DailyPayrollAbsencePayload {
    minutesAbsence: number;
    absenceTypeOid: string;
}

export interface PayrollPayload {
    oid: string;
    name: string;
    value: number | boolean;
}

export interface RetrieveDailyPayrollRequest {
    locator: NextLocator;
	date: string;
	planningAreas: string[];
	userIds: string[];
	supervisorIds: string[];
    rol: string[];
    contractType: string[];
	discardValidated: boolean;
	discardNonValidated: boolean;
}

export interface RetrieveDailyPayrollReply extends JsonReply {
    dailyPayroll: DailyPayroll[];
    partaker: Partaker;
}

export interface RetrieveMonthlyPayrollRequest {
    locator: NextLocator;
	year: number;
    period: number;
	planningAreas: string[];
	userIds: string[];
	supervisorIds: string[];
    rol: string[];
    contractType: string[];
	discardValidated: boolean;
	discardNonValidated: boolean;
}

export interface RetrieveMonthlyPayrollReply extends JsonReply {
    monthlyPayroll: MonthlyPayroll[];
    partaker: Partaker;
}

export interface FlushDailyPayrollRequest {
    dailyPayroll: DailyPayroll[];
}

export interface FlushMonthlyPayrollRequest {
    monthlyPayroll: MonthlyPayroll[];
}

export interface RetrieveDailyPayrollSummaryRequest {
    locator: NextLocator;
    date: string;
}

export interface RetrieveDailyPayrollSummaryReply extends JsonReply {
    summaryPerPlanningArea: DailyPayrollSummaryPerPlanningArea;
    summaryPerPlanningAreaAndSupervisor: DailyPayrollSummaryPerPlanningAreaAndSupervisors;
    partaker: Partaker;
}

export interface RetrieveMonthlyPayrollSummaryRequest {
    locator: NextLocator;
    year: number;
    period: number;
}

export interface RetrieveMonthlyPayrollSummaryReply extends JsonReply {
    summaryPerPlanningArea: MonthlyPayrollSummaryPerPlanningArea;
    summaryPerPlanningAreaAndSupervisor: MonthlyPayrollSummaryPerPlanningAreaAndSupervisors;
    partaker: Partaker;
}

export interface RetrieveDailyPayrollOptionsRequest {
    serviceId: string;
    date: string;
}

export interface RetrieveMonthlyPayrollOptionsRequest {
    serviceId: string;
    year: number;
    period: number;
}

export interface RetrievePayrollOptionsReply extends JsonReply {
    planningArea: SelectOption[];
    userIds: SelectOption[];
    supervisorIds: SelectOption[];
    contractType: SelectOption[];
    rol: SelectOption[];
}

export interface DailyPayrollSummaryPerPlanningArea {
    [area: string]: DailyPayrollSummary;
}

export interface DailyPayrollSummaryPerPlanningAreaAndSupervisors {
    [area: string]: {
        [supervisor: string]: DailyPayrollSummary;
    };
}

export interface DailyPayrollSummary {
    assignments: number;
    supervisors: number;
    validated: number;
    withWorkday?: number;
    supervisorName?: string;
}

export interface MonthlyPayrollSummaryPerPlanningArea {
    [area: string]: DailyPayrollSummary;
}

export interface MonthlyPayrollSummaryPerPlanningAreaAndSupervisors {
    [area: string]: {
        [supervisor: string]: DailyPayrollSummary;
    };
}

export interface DownloadSearchAbsencesXSSFRequest {
    retrieveAbsenceForms: RetrieveAbsenceFormsRequest;
    language: string;
}

export interface ServiceWorkplaceUserAssignment {
    belongsTo?: string;
    serviceId?: string;
    serviceName?: string;
    workplaceCode?: string;
    workplaceName?: string;
    userId: string;
    userName?: string;
    sAMAccountName: string;
    jobName?: string;
    manualJobName: string[];
    companyId?: string;
    subrogacion?: boolean;
    semana?: number;
    dia?: number;
    lunes?: number;
    martes?: number;
    miercoles?: number;
    jueves?: number;
    viernes?: number;
    sabado?: number;
    domingo?: number;
    fechaAntiguedad?: string;
    expectedEndDate?: string;
    clienteTipoAsignacion?: ClienteTipoAsignacionEnum;
    deprecated: boolean;
    assignmentDate?: string;
    deprecatedDate?: string;
    lastUpdate?: string;
    lastUpdateUserId?: string;
}

export interface RetrieveServiceWorkplaceUserAssignmentBody {
    serviceId: string;
    date: string;
}

export interface RetrieveServiceWorkplaceUserAssignmentReply extends JsonReply {
    assignments: ServiceWorkplaceUserAssignment[];
    partaker: Partaker;
}

export interface ChangeServiceWorkplaceUserAssignmentJobRequest {
    serviceId: string;
    workplaceCode: string;
    userId: string;
    manualJobName: string[];
}

export interface SendQRToMissionUserRequest {
    serviceId: string;
    workplaceCode: string;
    userId: string;
}

export interface SynchronizeServiceWorkplaceUserAssignmentRequest {
    serviceId: string;
    mails: string[];
    date: string;
}
